import type {
  BarChartRow,
  LineTableRow,
  MapTableRow,
  GenericTableData,
  TieredBarTableRow,
  QuadrantScatterTableRow,
} from "../types";

export const toSafeNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const toPeriodLabel = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const weeklyMatch = raw.match(/^(\d{4})-w(\d{1,2})$/i);
  if (weeklyMatch) {
    return `W${String(weeklyMatch[2] ?? "").padStart(2, "0")}`;
  }

  const monthlyMatch = raw.match(/^(\d{4})-(\d{2})$/);
  if (monthlyMatch) {
    const year = Number(monthlyMatch[1]);
    const monthIndex = Number(monthlyMatch[2]) - 1;
    if (Number.isInteger(year) && monthIndex >= 0 && monthIndex <= 11) {
      return new Date(year, monthIndex, 1).toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });
    }
  }

  return raw;
};

// A line point's `x` is a *period key*, and which of these four shapes the
// backend sends depends on the dashboard's Group By filter
// (analytics_backend query_service.py:740-751 / base_cases_service.py:405-418):
//   day   "2026-01-02"    week    "2026-W03"
//   month "2026-01"       quarter "Q1-2026"
const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_KEY_PATTERN = /^\d{4}-\d{2}$/;

/**
 * Period key → the MM/DD/YYYY the table view and its CSV/XLS downloads show.
 *
 * Only the two shapes that land on a real calendar position are rewritten: a
 * week or a quarter covers a range, so printing one as a single date would be
 * a lie — those pass through as the backend sent them ("2026-W03", "Q1-2026").
 *
 * Sliced rather than parsed through a Date on purpose: `new Date("2026-01-02")`
 * is read as UTC midnight, which renders as Jan 1 everywhere west of GMT.
 * The regex has already proved the shape, so the digits can be moved directly.
 *
 * MM/DD/YYYY matches what the backend's own widget download writes
 * (`%m/%d/%Y`, analytics_backend dashboards.py:673), so a chart converted to a
 * table here and the same data downloaded from the server read the same.
 */
export const toTableDateLabel = (value: unknown): string => {
  const raw = String(value ?? "").trim();

  if (DAY_KEY_PATTERN.test(raw)) {
    return `${raw.slice(5, 7)}/${raw.slice(8, 10)}/${raw.slice(0, 4)}`;
  }

  if (MONTH_KEY_PATTERN.test(raw)) {
    return `${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
  }

  return raw;
};

type PieDrilldownTopLevelPoint = {
  name?: string;
  y?: number | string;
  percentage?: number | string;
  value?: number | string;
  // Backend-supplied per-row hex color (e.g. the "bar" chart_type API) —
  // threaded straight through to the bar renderer, which falls back to the
  // chart's default series color when it's absent.
  color?: string;
};

type PieDrilldownData = {
  series?: Array<{
    name?: string;
    data?: PieDrilldownTopLevelPoint[];
  }>;
};

type LineSeriesPoint = {
  x?: string | number;
  y?: number | string;
};

type LineSeries = {
  name?: string;
  data?: LineSeriesPoint[];
};

export const getTopLevelRows = (rawData: unknown) => {
  if (Array.isArray(rawData)) {
    return rawData.map((point) => {
      const item = point as PieDrilldownTopLevelPoint;
      const count =
        item.value !== undefined
          ? toSafeNumber(item.value)
          : toSafeNumber(item.y);
      const percentage =
        item.percentage !== undefined
          ? toSafeNumber(item.percentage)
          : toSafeNumber(item.y);
      return { category: String(item.name ?? ""), count, percentage, color: item.color };
    });
  }

  const data = rawData as PieDrilldownData;
  const points = data?.series?.[0]?.data ?? [];
  const total = points.reduce((sum, point) => sum + toSafeNumber(point.y), 0);

  return points.map((point) => ({
    category: String(point.name ?? ""),
    count: toSafeNumber(point.y),
    percentage:
      point.percentage !== undefined
        ? toSafeNumber(point.percentage)
        : total > 0
          ? (toSafeNumber(point.y) / total) * 100
          : 0,
    color: point.color,
  }));
};

export const getLineRows = (rawData: unknown): LineTableRow[] => {
  if (!Array.isArray(rawData)) return [];
  return (rawData as LineSeries[]).flatMap((series) => {
    const seriesName = String(series?.name ?? "");
    return (series?.data ?? []).map((point) => ({
      series: seriesName,
      // The x is a period key ("2026-01-02"); the table shows it as a date.
      category: toTableDateLabel(point?.x),
      value: toSafeNumber(point?.y),
    }));
  });
};

export const getMapRows = (rawData: unknown): MapTableRow[] => {
  if (!Array.isArray(rawData)) return [];
  return rawData.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      state: String(row.State ?? row.state ?? row.name ?? ""),
      cases: toSafeNumber(row.Cases ?? row.cases ?? row.value ?? row.y),
    };
  });
};

export const getGenericTableData = (rawData: unknown): GenericTableData => {
  if (!Array.isArray(rawData)) return { headers: [], rows: [] };
  const rows = rawData as Array<Record<string, unknown>>;
  const headers = rows[0] ? Object.keys(rows[0]) : [];
  return { headers, rows };
};

const getCategoryValueColumnKeys = (
  headers: string[],
  rows: Array<Record<string, unknown>>,
): { categoryKey: string; valueKey: string } | null => {
  if (!headers.length || !rows.length) return null;
  const valueKey = headers.find((headerKey) =>
    rows.some((row) => Number.isFinite(Number(row[headerKey]))),
  );
  if (!valueKey) return null;
  const categoryKey = headers.find((headerKey) => headerKey !== valueKey);
  return { categoryKey: categoryKey ?? headers[0] ?? "", valueKey };
};

export const getDataTableChartRows = (rawData: unknown): BarChartRow[] => {
  const tableData = getGenericTableData(rawData);
  const keys = getCategoryValueColumnKeys(tableData.headers, tableData.rows);
  if (!keys) return [];
  return tableData.rows.map((row) => ({
    name: String(row[keys.categoryKey] ?? ""),
    value: toSafeNumber(row[keys.valueKey]),
  }));
};

export const getBarRows = (
  rawData: unknown,
  sourceChartType: string,
): BarChartRow[] => {
  if (sourceChartType === "map") {
    return getMapRows(rawData).map((row) => ({
      name: row.state,
      value: row.cases,
    }));
  }
  if (sourceChartType === "line" || sourceChartType === "multi_line") return [];
  if (sourceChartType === "data_table") return getDataTableChartRows(rawData);
  if (sourceChartType === "funnel") {
    const funnelData = rawData as {
      stages?: Array<{ name: string; count: number }>;
    };
    return (funnelData?.stages ?? []).map((stage) => ({
      name: stage.name,
      value: stage.count,
    }));
  }
  return getTopLevelRows(rawData).map((row) => ({
    name: row.category,
    value: row.count,
    percentage: row.percentage,
    color: row.color,
  }));
};

export const getPieRows = (rawData: unknown, sourceChartType: string) => {
  const baseRows = getBarRows(rawData, sourceChartType);
  const total = baseRows.reduce(
    (sum, row) => sum + toSafeNumber(row.value),
    0,
  );
  return baseRows.map((row) => ({
    ...row,
    percentage: total > 0 ? (toSafeNumber(row.value) / total) * 100 : 0,
  }));
};

export const getTieredBarRows = (rawData: unknown): TieredBarTableRow[] => {
  const records = Array.isArray(rawData)
    ? rawData
    : Array.isArray((rawData as any)?.records)
      ? (rawData as any).records
      : [];
  return (records as Array<Record<string, unknown>>)
    .map((r) => ({
      prescriber: String(r.prescriber ?? ""),
      pts: toSafeNumber(r.pts),
      pa_percentage: String(r.pa_percentage ?? ""),
      abandon_percentage: String(r.abandon_percentage ?? ""),
      tier: String(r.tier ?? ""),
    }))
    .sort((a, b) => b.pts - a.pts);
};

export const getQuadrantScatterRows = (
  rawData: unknown,
): QuadrantScatterTableRow[] => {
  const arr = Array.isArray(rawData)
    ? rawData
    : Array.isArray((rawData as any)?.practices)
      ? (rawData as any).practices
      : [];
  return (arr as Array<Record<string, unknown>>)
    .map((r) => ({
      practice: String(r.practice ?? ""),
      pts: toSafeNumber(r.pts),
      pa_cases: toSafeNumber(r.pa_cases),
      pa_approved_cases: toSafeNumber(r.pa_approved_cases),
      pa_percentage: String(r.pa_percentage ?? "0.0%"),
      abandon_percentage: String(r.abandon_percentage ?? "0.0%"),
      tier: String(r.tier ?? ""),
    }))
    .sort((a, b) => b.pts - a.pts);
};
